var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { FeedbackDissatisfiedIcon, FeedbackHappyIcon, FeedbackNeutralIcon, FeedbackSadIcon, FeedbackSatisfiedIcon } from '@inter-ikea-kompis/icons';
import { IconPill, IconPillGroup } from '@inter-ikea-kompis/component-icon-pill';
import { IconPillSizeOptionsEnum } from '@inter-ikea-kompis/component-icon-pill';
import { Text } from '@inter-ikea-kompis/component-text';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { crossSmall, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import MiniSurveyAnswerEnum from '../../enums/MiniSurveyAnswerEnum.js';
import MiniSurveyLayoutEnum from '../../enums/MiniSurveyLayoutEnum.js';
import MiniSurveyStateEnum from '../../enums/MiniSurveyStateEnum.js';
import MiniSurveyCardStyle from '../mini-survey-card/MiniSurveyCardStyle.js';
/**
 * Mini Survey component.
 *
 * @event close Click on close button
 * @event select Emitted when user selects a smiley or binary choice
 * @event reset Emitted when component should reset
 */
export default class MiniSurveyCard extends KompisElement {
    constructor() {
        super(...arguments);
        // The layout of the Mini Survey, can be set to grade or binary
        this.layout = MiniSurveyLayoutEnum.grade;
        // Localized information. Deprecated - use "translations" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // Valid states can be found in MiniSurveyStateEnum
        this.state = MiniSurveyStateEnum.default;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations  to be required, but since the props to provide translations is optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslations()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${MiniSurveyCardStyle.getStyle({ theme: this.theme })}
			</style>
			<div class="container">
				${this.getHeaderContainer()} ${this.getQuestionContainer()}
				${this.layout === MiniSurveyLayoutEnum.grade
            ? this.getSmileyContainer()
            : this.getBinaryChoiceContainer()}
			</div>
		`;
    }
    async connectedCallback() {
        registerIcon(crossSmall);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-icon-pill', IconPill);
        this.customElements.define('kompis-icon-pill-group', IconPillGroup);
        this.customElements.define('kompis-text', Text);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        clearTimeout(this.timeout);
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('state')) {
            if (this.state === MiniSurveyStateEnum.success) {
                const miniSurveyCard = this.shadowRoot?.querySelector('.headerContainer');
                if (miniSurveyCard) {
                    miniSurveyCard.focus();
                }
                clearTimeout(this.timeout);
                this.timeout = setTimeout(() => this.dispatchEvent(new CustomEvent('reset', {
                    bubbles: true,
                    detail: {
                        state: MiniSurveyStateEnum.default
                    }
                })), 30000);
            }
        }
        return true;
    }
    hasTranslations() {
        return !!this.translations || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    /**
     * @returns - Html.
     */
    getHeaderContainer() {
        const { miniSurveyHeader, miniSurveySuccessHeader, modalAriaLabelButtonClose } = this.getTranslations();
        const headerText = this.state !== MiniSurveyStateEnum.success ? miniSurveyHeader : miniSurveySuccessHeader;
        const tertiaryVariant = 'tertiary';
        return html `
			<div class="headerContainer" tabindex="-1">
				<kompis-text .theme="${this.theme}" .type="${ThemeFontStyleTypeEnum.headingSmall}"
					>${headerText}</kompis-text
				>
				<skapa-icon-button
					size="xsmall"
					variant="${tertiaryVariant}"
					@click="${this.onCloseClick.bind(this)}"
				>
					<skapa-icon slot="icon" icon="cross-small"></skapa-icon>
					${modalAriaLabelButtonClose}
				</skapa-icon-button>
			</div>
		`;
    }
    /**
     * @returns - Html.
     */
    getQuestionContainer() {
        const questionText = this.state !== MiniSurveyStateEnum.success
            ? this.questionText
            : this.getTranslations().miniSurveySuccessBody;
        return html `
			<kompis-text class="question" .theme="${this.theme}">${questionText}</kompis-text>
		`;
    }
    /**
     * @returns - Html.
     */
    getSmileyContainer() {
        if (this.state === MiniSurveyStateEnum.success) {
            return null;
        }
        const iconArray = [
            FeedbackSadIcon,
            FeedbackDissatisfiedIcon,
            FeedbackNeutralIcon,
            FeedbackSatisfiedIcon,
            FeedbackHappyIcon
        ];
        return html `
			<div class="smileyContainer">
				<kompis-icon-pill-group .theme="${this.theme}">
					${iconArray.map((icon, index) => {
            return html `
							<kompis-icon-pill
								@select="${this.onSelectClick.bind(this)}"
								id="${index}"
								.iconPillSize="${IconPillSizeOptionsEnum.medium}"
								.icon="${icon}"
								.theme="${this.theme}"
							></kompis-icon-pill>
						`;
        })}
				</kompis-icon-pill-group>
			</div>
		`;
    }
    /**
     * @returns - Html.
     */
    getBinaryChoiceContainer() {
        if (this.state === MiniSurveyStateEnum.success) {
            return null;
        }
        const primaryVariant = 'primary';
        const secondaryVariant = 'secondary';
        return html `
			<div class="binaryChoiceContainer">
				<skapa-button
					@click="${this.onNoClicked.bind(this)}"
					size="small"
					variant="${secondaryVariant}"
				>
					${this.getTranslations().no}
				</skapa-button>
				<skapa-button
					@click="${this.onYesClicked.bind(this)}"
					size="small"
					variant="${primaryVariant}"
				>
					${this.getTranslations().yes}
				</skapa-button>
			</div>
		`;
    }
    /**
     * Handler for close button.
     */
    onCloseClick() {
        this.dispatchEvent(new CustomEvent('close', {
            bubbles: true,
            detail: {
                type: 'icon-button',
                visible: false
            }
        }));
    }
    /**
     * Handler for select icon pill.
     *
     * @param event ...
     */
    onSelectClick(event) {
        this.dispatchEvent(new CustomEvent('select', {
            bubbles: true,
            detail: {
                type: 'icon-pill',
                grade: Number(event.target.id) + 1,
                state: MiniSurveyStateEnum.success
            }
        }));
    }
    /**
     * Handler for "yes" button.
     */
    onYesClicked() {
        this.dispatchEvent(new CustomEvent('select', {
            bubbles: true,
            detail: {
                type: 'click',
                answer: MiniSurveyAnswerEnum.yes,
                state: MiniSurveyStateEnum.success
            }
        }));
    }
    /**
     * Handler for "no" button.
     */
    onNoClicked() {
        this.dispatchEvent(new CustomEvent('select', {
            bubbles: true,
            detail: {
                type: 'click',
                answer: MiniSurveyAnswerEnum.no,
                state: MiniSurveyStateEnum.success
            }
        }));
    }
}
__decorate([
    property({ type: Object, required: true })
], MiniSurveyCard.prototype, "theme", void 0);
__decorate([
    property({ type: String })
], MiniSurveyCard.prototype, "layout", void 0);
__decorate([
    property({ type: Object })
], MiniSurveyCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], MiniSurveyCard.prototype, "translations", void 0);
__decorate([
    property({ type: String })
], MiniSurveyCard.prototype, "questionText", void 0);
__decorate([
    property({ type: String })
], MiniSurveyCard.prototype, "state", void 0);
//# sourceMappingURL=MiniSurveyCard.js.map